import { DateTime, Duration } from 'luxon';

/**
 * Extends the NSwag-generated Client to wire up jsonParseReviver,
 * converting DateTime and Duration fields automatically during JSON.parse
 * based on field name conventions (same as value-type-converter.functions.ts).
 */

export const bookerJsonParseReviver = (key: string, value: unknown): unknown => {
    // Arrays of ISO duration strings (e.g. GetProgramByIdDurationOptions.durations): the array elements
    // carry numeric keys, so they can't be matched by suffix individually — revive them here on the
    // owning property instead. Must run before the non-string guard below, which skips arrays.
    if (Array.isArray(value) && key.toLowerCase().endsWith('durations')) {
        return value.map((item) => (typeof item === 'string' && item.length > 0 ? Duration.fromISOTime(item) : item));
    }
    // Arrays of ISO time-of-day strings (e.g. StartPolicyDto.startTimes): same rationale as *durations
    // above. Elements that aren't strings (e.g. object arrays incidentally named "*Times") pass through
    // unchanged.
    if (Array.isArray(value) && key.toLowerCase().endsWith('times')) {
        return value.map((item) => (typeof item === 'string' && item.length > 0 ? Duration.fromISOTime(item) : item));
    }
    if (typeof value !== 'string' || value.length === 0) return value;
    const lk = key.toLowerCase();

    if (lk.endsWith('json')) {
        return JSON.parse(value, bookerJsonParseReviver);
    }
    // A '*Utc' suffix names a timestamp (addedUtc, stateChangedUtc), same as the 'At' suffix above.
    // Without it the generated client types the field as DateTime while it stays an ISO string at
    // runtime, and the first .toFormat() call on it throws.
    if (lk.endsWith('date') || lk.endsWith('datetime') || lk.endsWith('utc') || key.endsWith('At')) {
        return DateTime.fromISO(value);
    }
    if (lk.endsWith('time') || lk.endsWith('duration') || lk.endsWith('timeunit')) {
        return Duration.fromISOTime(value);
    }
    return value;
};
